import { AfterViewInit, Directive, ElementRef, forwardRef, HostListener, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { StringUtils } from '../../../../utils/string.utils';
import { MobileUtils } from '../../../../utils/mobile.utils';

export interface NumbersDirectiveHandler {
  element: ElementRef<HTMLInputElement>;
  precision: number;
  allowNegative: boolean;

  onAfterViewInit();

  onInput(onChange: (_) => void);

  onWriteValue(value: any);

  onKeyPress(event: KeyboardEvent);

  onBlur();

  onFocus();
}

export class MobileNumbersDirectiveHandler implements NumbersDirectiveHandler {

  allowNegative: boolean;
  precision: number;

  constructor(public element: ElementRef<HTMLInputElement>) {
  }

  onAfterViewInit() { }

  onBlur() { }

  onFocus() { }

  onInput(onChange: (_) => void) {
    onChange(this.element.nativeElement.value);
  }

  onKeyPress(event: KeyboardEvent) { }

  onWriteValue(value: any) {
    this.element.nativeElement.value = value;
  }
}

export class DesktopNumbersDirectiveHandler implements NumbersDirectiveHandler {

  private readonly NUMBER_REGEX: RegExp = /^\d|\.|,$/;
  private readonly NOT_NUMBER_REGEX: RegExp = /[^\d.,]/g;
  private readonly LEADING_ZEROS_CLEAR_REGEX: RegExp = /^0+(?!(,|\.|$))/;

  precision: number;
  allowNegative: boolean;


  constructor(public element: ElementRef<HTMLInputElement>) {
  }

  onAfterViewInit(): void {
    this.format();
    this.addSpaces(this.precision);
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key.length !== 1) {
      return;
    }

    if (this.prevent(event.key)) {
      event.preventDefault();
    } else if (this.isDecimalPoint(event.key) && this.element.nativeElement.value.indexOf(',') > -1) {
      event.preventDefault();
    }
  }

  onWriteValue(value: any) {
    this.format(value);
    this.addSpaces(this.precision);
  }

  onInput(onChange: (_) => void) {
    this.format();
    onChange(this.parse());
  }

  onBlur() {
    this.removeLeadingZeros();
    this.addSpaces(this.precision);
  }

  onFocus() {
    this.removeWhitespaces();
  }

  private removeWhitespaces(): void {
    let value = this.element.nativeElement.value;

    value = value.replace(/\s+/g, '');

    this.element.nativeElement.value = value;
  }

  private prevent(char: string): boolean {
    if (this.allowNegative && this.element.nativeElement.selectionStart === 0) {
      return !this.isNumberOrDecimalPoint(char) && !this.isNegativeSign(char);
    } else {
      return !this.isNumberOrDecimalPoint(char);
    }
  }

  private isNumberOrDecimalPoint(char: string): boolean {
    return this.NUMBER_REGEX.test(char);
  }

  private isNegativeSign(char: string): boolean {
    return char === '-';
  }

  private format(value: string = this.element.nativeElement.value): void {
    if (value == null) {
      this.element.nativeElement.value = null;
      return;
    }

    if (typeof value !== 'string') {
      value = (value as any).toString();
    }

    value = this.clearInvalidCharacters(value);

    const selectionPos = this.element.nativeElement.selectionStart;
    if (value.indexOf('.') > -1) {
      value = value.replace('.', ',');
    }

    this.element.nativeElement.value = value;

    if (this.isFocused()) {
      this.element.nativeElement.setSelectionRange(selectionPos, selectionPos);
    }
  }

  private clearInvalidCharacters(value: string): string {
    if (this.allowNegative && this.isLeadingNegativeSign(value)) {
      return value.charAt(0) + value.substr(1).replace(this.NOT_NUMBER_REGEX, '');
    }
    return value.replace(this.NOT_NUMBER_REGEX, '');
  }

  private isLeadingNegativeSign(value: string): boolean {
    return value.charAt(0) === '-';
  }

  private isFocused(): boolean {
    return document.activeElement === this.element.nativeElement;
  }

  private parse(): number {
    const value = Number.parseFloat(this.element.nativeElement.value.replace(',', '.'));
    return isNaN(value) ? null : value;
  }

  private isDecimalPoint(char: string) {
    return char === '.' || char === ',';
  }

  private removeLeadingZeros(): void {
    this.element.nativeElement.value = this.element.nativeElement.value.replace(this.LEADING_ZEROS_CLEAR_REGEX, '');
  }

  private addSpaces(precision: number) {
    let value = this.element.nativeElement.value;

    value = value.replace(/\s+/g, '');
    const decimalPointPos = StringUtils.indexOfDecimalPoint(value, ',');

    let decimalPart = value.substring(decimalPointPos);
    if (precision && !StringUtils.isEmpty(value)) {

      if (decimalPart.length === 0) {
        decimalPart = ',';
      }

      while (decimalPart.length - 1 < precision) {
        decimalPart = decimalPart.concat('0');
      }

      value = value.substring(0, decimalPointPos).concat(decimalPart);
    }

    value = StringUtils.insertSeparators(value.substring(0, decimalPointPos), ' ') + value.substring(decimalPointPos);

    this.element.nativeElement.value = value;
  }
}

@Directive({
  selector: 'input[numbers]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumbersDirective),
      multi: true
    }
  ]
})
export class NumbersDirective implements ControlValueAccessor, AfterViewInit {

  @Input()
  set precision(value: number) {
    this.handler.precision = value;
  }
  @Input()
  set allowNegative(value: boolean) {
    this.handler.allowNegative = value.toString() === 'true';
  }


  handler: NumbersDirectiveHandler;

  onChange: (_) => void;

  constructor(private element: ElementRef<HTMLInputElement>) {
    if (MobileUtils.isMobile) {
      element.nativeElement.type = 'number';
      this.handler = new MobileNumbersDirectiveHandler(element);
    } else {
      element.nativeElement.type = 'text';
      this.handler = new DesktopNumbersDirectiveHandler(element);
    }
  }

  ngAfterViewInit(): void {
    this.handler.onAfterViewInit();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.element.nativeElement.onblur = fn;
  }

  writeValue(value: any): void {
    this.handler.onWriteValue(value);
  }

  @HostListener('keypress', ['$event'])
  onKeyPress(event: KeyboardEvent) {
    this.handler.onKeyPress(event);
  }

  @HostListener('input')
  onInput() {
    this.handler.onInput(this.onChange);
  }

  @HostListener('blur')
  onBlur() {
    this.handler.onBlur();
  }

  @HostListener('focus')
  onFocus() {
    this.handler.onFocus();
  }
}
